// frontend/src/components/demo/DemoSidebar.tsx
// Floating role-switcher — visible only on demo tenants.
import { useEffect, useState } from 'react'
import useAuthStore from '../../store/authStore.js'
import { useLanguage } from '../../i18n/index.jsx'
import { ErrorBanner } from '../ui/index.jsx'

type DemoRole = 'local_admin' | 'manager' | 'staff' | 'viewer'

const ROLES: DemoRole[] = ['local_admin', 'manager', 'staff', 'viewer']

const ROLE_LABEL_KEYS: Record<DemoRole, string> = {
  local_admin: 'demoRoleLocalAdmin',
  manager:     'demoRoleManager',
  staff:       'demoRoleStaff',
  viewer:      'demoRoleViewer',
}

export default function DemoSidebar() {
  const { t }          = useLanguage()
  const tenant         = useAuthStore(s => s.tenant)
  const user           = useAuthStore(s => s.user)
  const switchDemoRole = useAuthStore(s => s.switchDemoRole)

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sw-demo-sidebar-collapsed') === 'true'
  )
  const [switching, setSwitching] = useState(false)
  // Inline error banner for switch failures. Auto-dismiss after 4s.
  const [error, setError] = useState<string | null>(null)
  useEffect(() => {
    if (!error) return
    const timer = setTimeout(() => setError(null), 4000)
    return () => clearTimeout(timer)
  }, [error])

  if (!tenant?.settings?.is_demo) return null

  function toggleCollapse() {
    const next = !collapsed
    setCollapsed(next)
    localStorage.setItem('sw-demo-sidebar-collapsed', String(next))
  }

  async function handleSwitch(role: DemoRole) {
    if (switching || role === user?.role) return
    setSwitching(true)
    setError(null)
    try {
      await switchDemoRole(role)
    } catch {
      setError(t('demoSwitchError'))
    } finally {
      setSwitching(false)
    }
  }

  const currentLabel = user?.role ? (t(ROLE_LABEL_KEYS[user.role as DemoRole]) || user.role) : ''

  return (
    <aside
      data-testid="demo-sidebar"
      className={`demo-sidebar${collapsed ? ' demo-sidebar--collapsed' : ''}`}
      style={{
        position: 'fixed',
        right: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
        background: 'var(--color-surface, #1e1e2e)',
        border: '1px solid var(--color-border, #333)',
        borderRadius: collapsed ? '0.5rem 0 0 0.5rem' : '0.75rem 0 0 0.75rem',
        padding: collapsed ? '0.5rem' : '1rem',
        minWidth: collapsed ? '2.5rem' : '14rem',
        boxShadow: '-2px 0 16px rgba(0,0,0,0.3)',
        transition: 'all 0.2s ease',
      }}
    >
      <button
        data-testid="demo-sidebar-toggle"
        onClick={toggleCollapse}
        style={{
          alignSelf: 'flex-start',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '0.25rem',
          color: 'var(--color-text-muted, #888)',
          fontSize: '1rem',
        }}
        aria-label={collapsed ? 'Expand role switcher' : 'Collapse role switcher'}
      >
        {collapsed ? '◀' : '▶'}
      </button>

      {!collapsed && (
        <>
          <div style={{
            fontSize: '0.7rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: 'var(--color-text-muted, #888)',
          }}>
            {t('demoSwitchRole')}
          </div>

          {currentLabel && (
            <div
              data-testid="demo-current-role"
              style={{
                fontSize: '0.75rem',
                color: 'var(--color-text, #eee)',
                marginBottom: '0.25rem',
              }}
            >
              {(t('demoYouAreRole')).replace('{role}', currentLabel)}
            </div>
          )}

          {error && (
            <ErrorBanner
              data-testid="demo-switch-error"
              style={{ fontSize: '0.7rem' }}
            >
              {error}
            </ErrorBanner>
          )}

          {ROLES.map(role => {
            const isActive = user?.role === role
            return (
              <button
                key={role}
                data-testid={`role-card-${role}`}
                onClick={() => handleSwitch(role)}
                disabled={switching}
                style={{
                  textAlign: 'left',
                  padding: '0.5rem 0.75rem',
                  borderRadius: '0.5rem',
                  border: isActive
                    ? '2px solid var(--color-primary, #7c3aed)'
                    : '1px solid var(--color-border, #333)',
                  background: isActive
                    ? 'var(--color-primary-muted, rgba(124,58,237,0.15))'
                    : 'transparent',
                  cursor: switching ? 'wait' : isActive ? 'default' : 'pointer',
                  opacity: switching && !isActive ? 0.6 : 1,
                  transition: 'all 0.15s',
                  color: isActive ? 'var(--color-primary, #7c3aed)' : 'var(--color-text, #eee)',
                  fontSize: '0.8rem',
                  fontWeight: isActive ? 600 : 500,
                }}
              >
                {t(ROLE_LABEL_KEYS[role]) || role}
              </button>
            )
          })}
        </>
      )}
    </aside>
  )
}
